import React, {useState, useEffect, useRef} from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Scrollbar } from "react-scrollbars-custom";
import {isAdminSelector} from "../../store/auth/selectors";
import close from "../../images/newDesign/closeBlack.svg";
import plus from "../../images/newDesign/plus_in_circle.svg";
import minus from "../../images/newDesign/minus_in_circle.svg";
import { StyledAutoCampaignList } from "./AutoCampaign.styled";
import { AutoCampaignCard } from "./AutoCampaignCard";
import Checkbox from "../../common/simple/Checkbox";
import PrintPdfIcons from "../../common/export_documents/PrintPdfIcons";
import {constants} from "../../common/constants/constants";
import dayjs from "dayjs";
import {shortenText} from "../../common/shared/statelessMethods";

const EMPTY_FIELD = '---';

export function AutoCampaignList({buildingID, records, inMonitoring, onAptNotesOpen, removeFromCampaign, addToCampaign}) {
    const { t } = useTranslation();
    const isAdmin = useSelector(isAdminSelector);
    const ltr = useSelector(state => state.common.ltrEnabled);

    const [totalApts, setTotalApts] = useState(0);
    const [selectAllChecked, setSelectAllChecked] = useState(false);
    const [showSelectedItemsPopUp, setShowSelectedItemsPopUp] = useState(false);
    const [popUpExited, setPopUpExited] = useState(false);
    const [selectedItemsNum, setSelectedItemsNum] = useState(0);
    const [checkedApartments, setCheckedApartments] = useState([]);
    const [titleRow, setTitleRow] = useState({
        apartmentNumber: t('reports.apartmentName'),
        name: t('reports.tenantName'),
        phone: t('reports.tenantPhone'),
        notificationsEmail: t('notifications.email'),
        paymentDate: t('reports.lastTurn')
    });

    const pdfUrl = 'https://bllink-public.s3.amazonaws.com/%D7%A0%D7%95%D7%A1%D7%97+%D7%94%D7%95%D7%93%D7%A2%D7%95%D7%AA+%D7%A7%D7%9E%D7%A4%D7%99%D7%99%D7%9F+%D7%90%D7%95%D7%98%D7%95%D7%9E%D7%98%D7%99.pdf';

    useEffect(() => {
        setTotalApts(records?.length);
    }, [records]);

    useEffect(() => {
        if (selectAllChecked) {
            setShowSelectedItemsPopUp(true);
        } else {
            setShowSelectedItemsPopUp(false);
        }
    }, [selectAllChecked]);

    useEffect(()=>{
        if (inMonitoring){
            setTitleRow({...titleRow, messageType:t('reports.messageType'), nextFace : t('reports.nextFace')});
        } else{
            const newItem = {...titleRow};
            delete newItem.messageType;
            delete newItem.nextFace;
            setTitleRow(newItem);
        }
        setSelectedItemsNum(0);
        setSelectAllChecked(false);
    },[inMonitoring]);

    useEffect(() => {
        if (selectedItemsNum > 0) {
            setShowSelectedItemsPopUp(true);
        }else{
            setShowSelectedItemsPopUp(false);
        }
    }, [selectedItemsNum]);

    useEffect(() => {
        if (popUpExited) {
            setShowSelectedItemsPopUp(false);
            setSelectAllChecked(false);
            setPopUpExited(false);
        }
    }, [popUpExited]);



    function updateSelectedItems(increased, apartmentNumber) {
        if (increased) {
            setCheckedApartments(current => [...current, apartmentNumber]);
            setSelectedItemsNum(current => current + 1);
        } else if (selectedItemsNum > 0) {
            setCheckedApartments(current => current.filter((apt) => {
                return apt !== apartmentNumber
            }));
            setSelectedItemsNum(current => current - 1);
        }
    }

    function renderTitles() {
        return (
            <>
                <div>{t('reports.apartmentName')}</div>
                <div>{t('reports.tenantName')}</div>
                <div>{t('reports.tenantPhone')}</div>
                <div>{t('notifications.email')}</div>
                <div>{t('reports.lastTurn')}</div>
                {inMonitoring && <>
                    <div>{t('reports.messageType')}</div>
                    <div>{t('reports.nextFace')}</div>
                </>}
                <div>{t('reports.debtListActions.aptNotes')}</div>
            </>)

    }

    const closeItemsPopUp = () => {
        setPopUpExited(true);
    }
    const getCards = () => {
        const parsedRecords = getParsedRecords();
        return parsedRecords?.map((record) => {
            return <AutoCampaignCard apt={record} inMonitoring={inMonitoring} updateSelectedItems={updateSelectedItems} onAptNotesOpen= {onAptNotesOpen} selectAll={selectAllChecked} popUpExited = {popUpExited}/>;
        });
    };

    function handleSelectedItemsPopUpClick() {
        inMonitoring ? removeFromCampaign(checkedApartments) : addToCampaign(checkedApartments);
    }

    function getParsedRecords() {
        return records.map((record)=>{
            fillEmptyParameters(record);
            const tenantItem = getMailOrPhoneAction('tenantPhone', 'tenantEmail', record);
            const ownerItem = getMailOrPhoneAction('ownerPhone', 'ownerEmail', record);
            const ownerName = shortenText(record.OwnerName).shortComment;
            const tenantName = shortenText(record.Name).shortComment;
            return {
                apartmentNumber: record.ApartmentNum,
                name: [`${t('autoCampaign.labels.owner')}: ${ownerName}`, `${t('autoCampaign.labels.renter')}: ${tenantName}`],
                phone: [record.OwnerPhone, record.Phone],
                notificationsEmail: [record.OwnerEmail, record.Email],
                paymentDate: [ownerItem.lastActionDate,tenantItem.lastActionDate],
                messageType: [ownerItem.lastActionName,tenantItem.lastActionName],
                nextFace: [ownerItem.nextActionDate, tenantItem.nextActionDate]
            }
        });
    }
    function fillEmptyParameters(record) {
        record.OwnerName = record.OwnerName || EMPTY_FIELD;
        record.Name = record.Name || EMPTY_FIELD;
        record.OwnerPhone = record.OwnerPhone || EMPTY_FIELD;
        record.Phone = record.Phone || EMPTY_FIELD;
        record.OwnerEmail = record.OwnerEmail || EMPTY_FIELD;
        record.Email = record.Email || EMPTY_FIELD;
    }
    const getMailOrPhoneAction = (phoneString, mailString, apt) => {
        const item = {};
        if (apt.monitoring && (apt.monitoring[phoneString] || apt.monitoring[mailString])) {
            const type = getType(phoneString, mailString, apt);
            const lastActionName = apt.monitoring[type].lastActionName === 'welcome' ? 'welcome' : 'reminder';
            item.type = t(`reports.${type}`);
            item.lastActionDate = dayjs(apt.monitoring[type].lastActionDate).format('DD/MM/YYYY');
            item.lastActionName = t(`monitoring.activeMonitoring.actions.${lastActionName}`);
            item.nextActionDate = getNextActionIn(apt.monitoring[type].nextActionDate);
        } else {
            item.lastActionDate = EMPTY_FIELD;
            item.lastActionName = EMPTY_FIELD;
            item.nextActionDate = EMPTY_FIELD;
        }
        return item;
    }
    const getType = (phoneString, mailString,apt) => {
        if (!apt.monitoring[phoneString]) {
            return mailString;
        } else if (!apt.monitoring[mailString]) {
            return phoneString;
        } else {
            return Date.parse(apt.monitoring[phoneString].lastActionDate) > Date.parse(apt.monitoring[mailString].lastActionDate) ? phoneString : mailString;
        }
    }

    const getNextActionIn = (nextActionDateStr) => {
        const nextActionDate = dayjs(nextActionDateStr);
        const hours = nextActionDate.diff(Date.now(), 'hour');

        //moved due to ticket https://bllink.atlassian.net/jira/software/projects/SSB/boards/7?assignee=63df7122a5d0c826306ba6a5&selectedIssue=SSB-1322
        //const mustBeDays = hours >= 24;
        const mustBeDays = true;
        const nextActionInText = mustBeDays ? t('monitoring.activeMonitoring.labels.days') : t('monitoring.activeMonitoring.labels.hours');
        let nextActionIn;

        if (mustBeDays) {
            nextActionIn = Math.ceil(hours / 24);
        } else {
            if (hours === 0) {
                nextActionIn = '< 1';
            } else {
                nextActionIn = hours;
            }
        }

        return `${nextActionIn} ${nextActionInText}`;
    }

    return (
        <StyledAutoCampaignList inMonitoring={inMonitoring} ltr={ltr} className="bllink-text">
            <Scrollbar
                style={{ width: '100%', height: 'calc(100vh - 200px)' }}
                trackYProps={{
                    renderer: props => {
                        const { elementRef, ...restProps } = props;
                        return <div {...restProps} ref={elementRef} className="scroll-track" />;
                    }
                }}
                thumbYProps={{
                    renderer: props => {
                        const { elementRef, ...restProps } = props;
                        return <div {...restProps} ref={elementRef} className="scroll-thumb" />;
                    }
                }}
                rtl={false}
                noScrollX
            >
                <div className="list-header">
                    <div className='total-apartments'>
                        {t('reports.total_apartments')}: <span>{totalApts}</span>
                    </div>
                    {/*todo enable when pdf/excel will be ready to publish*/}
                    <div hidden={true}>
                        <PrintPdfIcons
                            classes={ltr ? "ml-auto" : "mr-auto"}
                            buildingID={buildingID}
                            rows={getParsedRecords}
                            titleRow={[titleRow]}
                            eventLabel={'managerAutoCampaign'}
                            titleString={t('reports.auto_campaign')}
                            reportName={t('buildings.nav_bar_auto_campaign')}
                            pdfSettings={{chunkSize: 18, orientation: constants.pages.pdfOrientation.landscape}}
                            pdfIframe={true}
                            hidePrint={true}
                        />
                    </div>
                    <a href={pdfUrl} target="_blank" rel="noreferrer"  download={"Auto Campaign"}>{t('reports.auto_campaign_instructions')}</a>
                </div>

                <div className="cards-list">
                    <div className={"titles"} ltr={ltr}>
                        <Checkbox
                            size={19}
                            checked={selectAllChecked}
                            onChange={() => setSelectAllChecked(!selectAllChecked)}
                            borderWidth={1}
                        />
                        <div className="cards-titles">
                            {renderTitles()}
                        </div>
                    </div>
                    <div className={"list-rows"}>
                        {getCards()}
                    </div>
                </div>
            </Scrollbar>

            {showSelectedItemsPopUp && (
                <div className="selected-items-pop-up">
                    <div className="items-selected">
                        <div className="quantity">{selectedItemsNum}</div> 
                        {t('reports.markedApartments')}
                    </div>

                    <div className="add-or-remove" onClick={handleSelectedItemsPopUpClick}>
                        <img src={inMonitoring ? minus : plus} />
                        <div>{inMonitoring ? t('reports.removeFromCampaign') : t('reports.addToCampaign')}</div>
                    </div>

                    <img className="close-items-pop-up" src={close} onClick={closeItemsPopUp} />
                </div>
            )}
        </StyledAutoCampaignList>
    )
}
